export type AutomationType = string; // Dynamiczne typy z backendu

export async function fetchAutomationTypes(): Promise<AutomationType[]> {
  const response = await fetch("/api/automations/types", { credentials: 'include' });
  if (!response.ok) {
    throw new Error("Failed to fetch automation types");
  }
  return response.json();
}

// Mapowanie typów na czytelne nazwy dla użytkownika
export const AUTOMATION_TYPE_LABELS: Record<AutomationType, string> = {
  REPLACE: "Zamiana zmiennych",
  CONVERT: "Konwersja do PDF",
  VALIDATE: "Sprawdzenie poprawności",
  SIGN: "Podpis cyfrowy",
  AUTO_FILL: "Automatyczne uzupełnienie",
};

// Funkcja zwracająca czytelną nazwę
export function getAutomationLabel(type: AutomationType): string {
  return AUTOMATION_TYPE_LABELS[type] || type; // Jeśli brak tłumaczenia, zwraca surową wartość
}

export interface AutomationFolder {
  id: string;
  name: string;
  createdAt?: string;
  parentId?: string | null;
}

export async function fetchAutomationFolders(tenantId: string, parentId?: string) {
  const url = new URL(`/api/tenants/${tenantId}/automation-folders`, window.location.origin);
  if (parentId !== undefined) {
    url.searchParams.set('parentId', parentId);
  }
  const response = await fetch(url.toString(), { credentials: 'include' });
  if (!response.ok) throw new Error('Błąd ładowania folderów automatyzacji');
  return response.json();
}

export async function addAutomationFolder(tenantId: string, name: string, parentId?: string | null) {
  const body: any = { name };
  if (typeof parentId === 'string' && parentId.length > 0) {
    body.parentId = parentId;
  }
  const response = await fetch(`/api/tenants/${tenantId}/automation-folders`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'include',
  });
  if (response.status === 409) {
    throw new Error('Folder o tej nazwie już istnieje w tej lokalizacji.');
  }
  if (!response.ok) throw new Error('Błąd dodawania folderu');
  return response.json();
}

export async function deleteAutomationFolder(tenantId: string, folderId: string) {
  const response = await fetch(`/api/tenants/${tenantId}/automation-folders/${folderId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Błąd usuwania folderu');
  return;
}

export async function addAutomation(tenantId: string, documentId: string, title: string, folderId?: string) {
  const response = await fetch(`/api/tenants/${tenantId}/automations`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ title, folderId, documentId }),
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error("Failed to add automation");
  }
}

export async function fetchAutomations(
  tenantId: string,
  page = 0,
  size = 10,
  searchQuery = "",
  sortBy = "createdAt",
  sortOrder = "desc",
  folderId?: string
) {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
    searchQuery: searchQuery,
    sortBy,
    sortOrder,
  });
  if (folderId) params.append('folderId', folderId);
  const response = await fetch(
    `/api/tenants/${tenantId}/automations?${params.toString()}`,
    { credentials: 'include' }
  );
  if (!response.ok) throw new Error("Błąd ładowania automatyzacji");
  return response.json();
}

export async function deleteAutomation(tenantId: string, automationId: string) {
  const response = await fetch(`/api/tenants/${tenantId}/automations/${automationId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Błąd usuwania automatyzacji');
  return;
}

export async function fetchVariablesForDocument(tenantId: string, automationId: string) {
  const response = await fetch(`/api/tenants/${tenantId}/automations/${automationId}/extract-variables`, { credentials: 'include' });
  if (!response.ok) throw new Error("Błąd ładowania zmiennych");
  return response.json();
}

export async function updateAutomation(tenantId: string, automationId: string, title: string, templateDocumentId: string, folderId?: string) {
  const response = await fetch(`/api/tenants/${tenantId}/automations/${automationId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title, templateDocumentId, folderId }),
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Błąd edycji automatyzacji');
  return;
}

export async function fetchFolderById(tenantId: string, folderId: string): Promise<AutomationFolder> {
  const response = await fetch(`/api/tenants/${tenantId}/automation-folders/${folderId}`, { credentials: 'include' });
  if (!response.ok) throw new Error('Błąd ładowania folderu');
  return response.json();
}

export interface Automation {
  id: string;
  title: string;
  type?: string;
  createdAt?: string;
  automationTemplateDocument?: {
    id: string;
    fileName?: string;
    title?: string;
  };
  autofillVariables?: string[];
  folderId?: string;
}

export const fetchAutomationById = async (tenantId: string, automationId: string): Promise<Automation> => {
  const response = await fetch(`/api/tenants/${tenantId}/automations/document/${automationId}`, { credentials: 'include' });
  if (!response.ok) {
    throw new Error('Failed to fetch automation');
  }
  return response.json();
};

export async function updateAutomationFolderName(tenantId: string, folderId: string, newName: string) {
  const response = await fetch(`/api/tenants/${tenantId}/automation-folders/${folderId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: newName }),
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Błąd edycji nazwy folderu');
  return response.json();
}

export interface AutomationWithFolderDto {
  id: string;
  title: string;
  createdAt: string;
  folderId: string | null;
  type: string;
  automationTemplateDocument?: {
    id: string;
    fileName?: string;
    s3Url?: string;
    title?: string;
  };
}

export interface AutomationFolderTreeDto {
  id: string;
  name: string;
  tenantId: string;
  createdAt: string;
  parentId: string | null;
  children: AutomationFolderTreeDto[];
  automations: AutomationWithFolderDto[];
}

export async function fetchAutomationTree(tenantId: string): Promise<AutomationFolderTreeDto[]> {
  const response = await fetch(`/api/tenants/${tenantId}/automations/tree`, { credentials: 'include' });
  if (!response.ok) throw new Error('Błąd ładowania drzewa automatyzacji');
  return response.json();
}

export async function searchAutomations(tenantId: string, query: string): Promise<AutomationWithFolderDto[]> {
  const response = await fetch(`/api/tenants/${tenantId}/automations/search?query=${encodeURIComponent(query)}`, { credentials: 'include' });
  if (!response.ok) throw new Error('Błąd wyszukiwania automatyzacji');
  return response.json();
}

export async function searchFreeTemplates(tenantId: string, query: string): Promise<{id: string, fileName: string}[]> {
  const response = await fetch(`/api/tenants/${tenantId}/free-templates-search?query=${encodeURIComponent(query)}`, { credentials: 'include' });
  if (!response.ok) throw new Error('Failed to fetch free templates');
  return await response.json();
}

export interface AutomationDto {
  id: string;
  title: string;
  type?: string;
  createdAt?: string;
  automationTemplateDocument?: { id: string; fileName?: string; title?: string };
  autofillVariables?: string[];
  folderId?: string;
}

export interface FolderTreeDto {
  id: string;
  name: string;
  tenantId: string;
  createdAt: string;
  parentId: string | null;
  automations: AutomationDto[];
  children: FolderTreeDto[];
  documents: any[];
}

export async function fetchAutomationFolderTreeById(tenantId: string, folderId: string): Promise<FolderTreeDto> {
  const response = await fetch(`/api/tenants/${tenantId}/automations/${folderId}`, { credentials: 'include' });
  if (!response.ok) throw new Error('Błąd ładowania folderu automatyzacji');
  return response.json();
}

